import {
  Box,
  Stack,
  Grid2,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Button,
  CircularProgress,
  Fade,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { GET } from "../../../request/service";
import { getDashboardEmbedUrl } from "../../../request/api";
import { useLocation } from "react-router-dom";
import {
  createEmbeddingContext,
  DashboardExperience,
  EmbeddingContext,
} from "amazon-quicksight-embedding-sdk";
import { IconRefresh } from "@tabler/icons-react";
import {
  NO_DATA,
  REPORT_TITLE,
  SHEET_TYPE_ID,
} from "../../../constants/dashboard";

interface Dashboard {
  DashboardId: string;
  Name: string;
}

const BPMPage: FC<{ dashboardList: any[] }> = (props) => {
  const [currentDashboard, setCurrentDashboard] = useState("");
  const [optionWidth, setOptionWidth] = useState("");
  const location = useLocation();
  const [embeddedDashboardExperience, setEmbeddedDashboardExperience] =
    useState<DashboardExperience | null>(null);
  const [dashboardList, setDashboardList] = useState<
    { DashboardId: string; Name: string }[]
  >(props.dashboardList ?? []);

  const [windowSize, setWindowSize] = useState({
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleChange = async (event: SelectChangeEvent) => {
    setCurrentDashboard(event.target.value);
    await getDashboardEmbedUrlData(event.target.value);
  };

  useEffect(() => {
    resetDashboardList();
    dashboardList.length > 0
      ? localStorage.setItem("dashboardList", JSON.stringify(dashboardList))
      : setDashboardList(
          JSON.parse(localStorage.getItem("dashboardList") || "[]"),
        );
  }, [location.pathname, embeddedDashboardExperience]);

  useEffect(() => {
    const dashboardStorageList = JSON.parse(
      localStorage.getItem("dashboardList") || "[]",
    );
    let autoWidth = 0;
    if (dashboardStorageList && dashboardStorageList.length > 0) {
      dashboardStorageList.map((dashboard: Dashboard) => {
        dashboard.Name.length > autoWidth
          ? (autoWidth = dashboard.Name.length)
          : null;
      });
      setCurrentDashboard(dashboardStorageList[0]?.DashboardId);
    } else {
      setCurrentDashboard(dashboardList[0]?.DashboardId);
    }

    setOptionWidth(autoWidth * 9.5 + "px");
    const dom = document.getElementById("dashboardContainer");
    if (dom) {
      setWindowSize({
        height:
          window.innerHeight -
          parseInt(dom.getBoundingClientRect().top.toString(), 10) -
          30,
      });
    }
  }, [optionWidth, window.innerHeight]);

  useEffect(() => {
    if (currentDashboard !== "") {
      getDashboardEmbedUrlData(currentDashboard);
    }
  }, [currentDashboard]);

  const getDashboardEmbedUrlData = async (dashboardId: string) => {
    if (dashboardId) {
      //getDashboardEmbedUrl
      const name = localStorage.getItem("userName");
      GET(
        getDashboardEmbedUrl + "?dashboardId=" + dashboardId + "&name=" + name,
        "",
      ).then(async (res: any) => {
        await showReport(res.EmbedUrl, dashboardId);

        // return res;
      });
    }
  };

  const resetDashboardList = () => {
    const existingList = document.getElementById("dashboard-select");
    if (
      existingList &&
      existingList.innerHTML === "" &&
      currentDashboard !== ""
    ) {
      setCurrentDashboard("");
    }
  };

  const showReport = async (embedDashboardUrl: string, dashboardId: string) => {
    setEmbeddedDashboardExperience(null);
    try {
      const embeddingContext: EmbeddingContext = await createEmbeddingContext();
      const { embedDashboard } = embeddingContext;

      const frameOptions = {
        url: embedDashboardUrl,
        container: "#dashboardContainer",
        width: "100%",
        height: `${windowSize.height}px`,
      };

      const contentOptions = {
        locale: "en-US",
        sheetOptions: {
          initialSheetId: dashboardId + SHEET_TYPE_ID.BPM,
          singleSheet: true,
          emitSizeChangedEventOnSheetChange: false,
        },
      };

      const existingReport = document.getElementById("dashboardContainer");

      if (existingReport) {
        try {
          existingReport.innerHTML = "";
          const embeddedDashboardExperience = await embedDashboard(
            frameOptions,
            contentOptions,
          );
          setEmbeddedDashboardExperience(embeddedDashboardExperience);
        } catch (error) {
          console.log("bpm embedDashboard error.", error);
        }
      }
    } catch (error) {
      console.log("bpm createEmbeddingContext error.", error);
    }
  };

  return (
    <>
      {optionWidth && (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              component="h1"
              fontSize="1.3rem"
              fontWeight={400}
              color="rgba(0, 0, 0, 0.6)"
            >
              {REPORT_TITLE.BPM}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            marginTop={"5px"}
            display={"inline-flex"}
            minWidth={optionWidth}
          >
            <FormControl
              sx={{
                m: 1,
                minWidth: optionWidth,
                margin: 0,
                marginRight: "20px",
              }}
              size="medium"
            >
              <InputLabel id="dashboard-input-label">Dashboards</InputLabel>
              <Select
                labelId="dashboard-select-label"
                id="dashboard-select"
                value={currentDashboard}
                label="Dashboard"
                onChange={handleChange}
                sx={{ minWidth: optionWidth }}
              >
                {dashboardList?.map(
                  (item: { DashboardId: string; Name: string }) => (
                    <MenuItem value={item.DashboardId}>
                      {item.Name ?? ""}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="large"
              onClick={async () =>
                await getDashboardEmbedUrlData(currentDashboard)
              }
              sx={{
                borderRadius: "50%",
                padding: "12px",
                minWidth: "auto",
                height: "auto",
                width: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <IconRefresh stroke={1.5} />
            </Button>
          </Stack>

          <Grid2
            sx={{
              marginTop: "10px",
              ".quicksight-embedding-iframe": {
                height: `${windowSize.height}px`,
              },
            }}
            container
            spacing={2}
            columns={16}
            id="dashboardContainer"
          ></Grid2>
          {embeddedDashboardExperience === null && currentDashboard !== "" ? (
            <>
              <Box
                sx={{
                  height: "78vh",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Fade
                  in={
                    embeddedDashboardExperience === null &&
                    currentDashboard !== ""
                  }
                  style={{
                    transitionDelay:
                      embeddedDashboardExperience === null &&
                      currentDashboard !== ""
                        ? "400ms"
                        : "0ms",
                  }}
                  unmountOnExit
                >
                  <CircularProgress />
                </Fade>
              </Box>
            </>
          ) : (
            <Grid2
              id={"no-bpm-data"}
              sx={{
                marginTop: "10px",
                height: `${windowSize.height}px`,
                width: "100%",
                display: currentDashboard === "" ? "grid" : "none",
                alignItems: "center",
                justifyItems: "center",
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                color: "rgba(0, 0, 0, 0.4)",
                fontSize: 40,
              }}
              container
              spacing={2}
              columns={16}
            >
              {NO_DATA.MESSAGE}
            </Grid2>
          )}
        </Box>
      )}
    </>
  );
};
export default BPMPage;
